import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import './customerMapTheme.css';
import { FaSpinner, FaMapMarkedAlt, FaSearch, FaChevronLeft, FaChevronRight, FaExclamationTriangle } from 'react-icons/fa';
import { useCustomers } from '../hooks/useCustomers';
import { geocodeAddress, hasAddress } from '../utils/geocode';
import CustomerMapDetailPanel from './customers/CustomerMapDetailPanel';

// Read-only map of customers, geocoded client-side from their address on
// file (LocationIQ — see utils/geocode.js — cached in localStorage). No
// coordinates are ever written back anywhere; this page only reads the same
// customer list the Customers tab already loads via useCustomers().

const AUSTRALIA_CENTER = [-25.2744, 133.7751];

// Standard free OSM raster tiles, darkened via CSS filter in
// customerMapTheme.css (.leaflet-tile-pane) to match the admin portal's
// gruvbox theme — no third-party dark-tile service/key needed.
const TILE_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
const TILE_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

// Dots are colored by NBN access technology (what the customer is actually
// connected via), not account status — the connection type is what admins
// care about at a glance on this page.
// Generated + validated with the dataviz skill's categorical-palette method
// (OKLCH, checked against the dark map surface #1d2021 with
// scripts/validate_palette.js --pairs all, since markers can land next to
// any other category on the map, not just "adjacent" in a fixed order).
// The old gruvbox-derived set failed lightness/chroma/CVD outright (several
// read as near-gray on this dark surface, worst pair ΔE 1.4). This set
// clears lightness, chroma, and contrast, and gets CVD separation into the
// legal warn band (6.3, was 1.4) — full all-pairs safety across 7
// categories isn't achievable (confirmed via an 80k-candidate search), so
// exact identity still leans on the legend labels and the popup's "Access
// Tech" text, not color alone.
const TECH_COLORS = {
  FTTP: '#46a462',
  FTTC: '#008970',
  FTTN: '#b48a22',
  FTTB: '#a55800',
  HFC: '#009ee5',
  'FIXED WIRELESS': '#5465b5',
  SATELLITE: '#d0579c',
};
const UNKNOWN_COLOR = '#928374';

const techColor = (tech) => {
  if (!tech) return UNKNOWN_COLOR;
  const key = String(tech).trim().toUpperCase();
  return TECH_COLORS[key] || UNKNOWN_COLOR;
};

// Precise = matched an actual house number. Imprecise (street/suburb-level
// only, common for AU addresses OSM hasn't fully mapped) uses the exact same
// dot — the faded/dashed treatment tried earlier was too subtle to notice on
// the map — plus a small "~" badge in the corner so it's still called out.
const dotIcon = (color, precise) =>
  L.divIcon({
    className: '',
    html: `
      <div style="position:relative;width:16px;height:16px;">
        <div style="width:16px;height:16px;border-radius:50%;background:${color};border:2px solid #1d2021;box-shadow:0 1px 4px rgba(0,0,0,0.6);"></div>
        ${precise ? '' : `<div style="position:absolute;top:-5px;right:-5px;width:11px;height:11px;border-radius:50%;background:#fabd2f;border:1.5px solid #1d2021;display:flex;align-items:center;justify-content:center;font-size:8px;font-weight:900;line-height:1;color:#1d2021;">~</div>`}
      </div>
    `,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
  });

// When zoomed out far enough that dots would overlap/be indistinguishable,
// group them into a numbered badge instead — clicking it zooms in to reveal
// the individual customers rather than hiding how many are there.
const clusterIcon = (cluster) => {
  const count = cluster.getChildCount();
  const size = count < 10 ? 32 : count < 50 ? 38 : 44;
  return L.divIcon({
    className: '',
    html: `<div style="width:${size}px;height:${size}px;border-radius:50%;background:#fabd2f;color:#1d2021;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:13px;border:2px solid #1d2021;box-shadow:0 1px 4px rgba(0,0,0,0.6);">${count}</div>`,
    iconSize: L.point(size, size, true),
  });
};

// Zooms/pans to fit all plotted markers once geocoding finishes — keeps the
// view relevant without the admin having to zoom out manually while there
// are only one or two customers.
const FitBounds = ({ markers }) => {
  const map = useMap();
  useEffect(() => {
    if (markers.length === 0) return;
    const bounds = L.latLngBounds(markers.map((m) => [m.lat, m.lon]));
    map.fitBounds(bounds, { padding: [50, 50], maxZoom: 13 });
  }, [markers.length, map]);
  return null;
};

const LegendItem = ({ color, label, approxBadge }) => (
  <div className="flex items-center gap-2 text-xs text-[#ebdbb2]">
    <span className="relative inline-block w-3 h-3 shrink-0">
      <span className="absolute inset-0 rounded-full" style={{ backgroundColor: color }} />
      {approxBadge && (
        <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-[#fabd2f] border border-[#1d2021]" />
      )}
    </span>
    {label}
  </div>
);

const markerKey = (customer) => customer.UID || customer.email;

const CustomerMap = () => {
  const { customers, loading: customersLoading, error: customersError } = useCustomers();
  const [markers, setMarkers] = useState([]);
  const [unmapped, setUnmapped] = useState([]);
  const [geocoding, setGeocoding] = useState(false);
  const [geocodedCount, setGeocodedCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [unmappedOpen, setUnmappedOpen] = useState(false);

  const markerRefs = useRef({});
  const clusterRef = useRef(null);
  const searchBoxRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(e.target)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!customers || customers.length === 0) {
      setMarkers([]);
      setUnmapped([]);
      return;
    }

    let cancelled = false;
    setMarkers([]);
    setUnmapped([]);
    setGeocoding(true);
    setGeocodedCount(0);

    // Resolve every address before committing anything to `markers` — adding
    // markers to the MarkerClusterGroup one at a time as geocoding streamed
    // in used to re-cluster the map mid-load, which silently closed any
    // popup the admin had already opened on an earlier marker. Batching the
    // update avoids that entirely; at this customer count the wait is a
    // couple of seconds, not worth the popup-stability risk.
    (async () => {
      const nextMarkers = [];
      const nextUnmapped = [];
      for (const customer of customers) {
        const coords = await geocodeAddress(customer);
        if (cancelled) return;
        if (coords) {
          nextMarkers.push({ customer, ...coords });
        } else {
          nextUnmapped.push({
            customer,
            reason: hasAddress(customer) ? 'address could not be located at all' : 'no address on file',
          });
        }
        setGeocodedCount((c) => c + 1);
      }
      if (cancelled) return;
      setMarkers(nextMarkers);
      setUnmapped(nextUnmapped);
      setGeocoding(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [customers]);

  const searchResults = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return [];
    const mappedKeys = new Set(markers.map((m) => markerKey(m.customer)));
    return (customers || [])
      .filter((c) => {
        const haystack = [c.fullName, c.email, c.accountNumber, c.address, c.street, c.suburb]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(q);
      })
      .slice(0, 8)
      .map((customer) => ({ customer, onMap: mappedKeys.has(markerKey(customer)) }));
  }, [searchQuery, customers, markers]);

  // Reveals the customer's marker (zooming out of its cluster if needed) and
  // opens its popup — the "search then pop up on the map" flow.
  const selectCustomer = (customer) => {
    const marker = markerRefs.current[markerKey(customer)];
    const cluster = clusterRef.current;
    if (marker && cluster) {
      cluster.zoomToShowLayer(marker, () => marker.openPopup());
    }
    setSearchOpen(false);
    setSearchQuery('');
  };

  if (customersLoading) {
    return (
      <div className="flex items-center justify-center h-96 text-[#928374]">
        <FaSpinner className="animate-spin mr-2" /> Loading customers…
      </div>
    );
  }

  if (customersError) {
    return <div className="text-[#fb4934] p-6">Failed to load customers: {customersError}</div>;
  }

  return (
    // 73px = AdminPortal's header height, 48px = the p-6 (24px top+bottom)
    // padding its <main> wraps renderContent() in — matching these exactly
    // avoids leftover page-level scroll under the "full screen" map.
    <div className="flex rounded-xl overflow-hidden border border-[#3c3836]" style={{ height: 'calc(100vh - 73px - 48px)' }}>
      <div className="relative flex-1 min-w-0">
        <MapContainer center={AUSTRALIA_CENTER} zoom={4} style={{ height: '100%', width: '100%', background: '#1d2021' }}>
          <TileLayer attribution={TILE_ATTRIBUTION} url={TILE_URL} />
          <FitBounds markers={markers} />
          <MarkerClusterGroup
            ref={clusterRef}
            iconCreateFunction={clusterIcon}
            maxClusterRadius={60}
            spiderfyOnMaxZoom
            showCoverageOnHover={false}
          >
            {markers.map(({ customer, lat, lon, precise }) => {
              const key = markerKey(customer);
              return (
                <Marker
                  key={key}
                  position={[lat, lon]}
                  icon={dotIcon(techColor(customer.vocusSqSnapshot?.accessTechnology), precise)}
                  ref={(el) => {
                    if (el) markerRefs.current[key] = el;
                    else delete markerRefs.current[key];
                  }}
                >
                  <Popup maxWidth={300} minWidth={280}>
                    <CustomerMapDetailPanel customer={customer} precise={precise} />
                  </Popup>
                </Marker>
              );
            })}
          </MarkerClusterGroup>
        </MapContainer>

        <div className="absolute top-3 left-3 bg-[#282828] border border-[#3c3836] rounded-lg px-3 py-2 z-[1000] flex items-center gap-3">
          <div className="flex items-center gap-2">
            <FaMapMarkedAlt className="text-[#fabd2f]" size={16} />
            <h2 className="text-sm font-bold text-[#ebdbb2]">Customer Map</h2>
          </div>
          <div className="text-xs text-[#928374] border-l border-[#3c3836] pl-3">
            {geocoding ? (
              <>
                <FaSpinner className="inline animate-spin mr-1" />
                Locating {geocodedCount} of {customers.length}…
              </>
            ) : (
              `${markers.length} of ${customers.length} plotted`
            )}
          </div>
        </div>

        <div className="absolute bottom-3 left-3 bg-[#282828] border border-[#3c3836] rounded-lg px-3 py-2 grid grid-cols-2 gap-x-4 gap-y-1 z-[1000]">
          {Object.entries(TECH_COLORS).map(([tech, color]) => (
            <LegendItem key={tech} color={color} label={tech} />
          ))}
          <LegendItem color={UNKNOWN_COLOR} label="Unknown" />
          <LegendItem color="#928374" label="Approximate location" approxBadge />
        </div>

        <div ref={searchBoxRef} className="absolute top-3 right-3 z-[1000] w-64">
          <div className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setSearchOpen(true);
              }}
              onFocus={() => setSearchOpen(true)}
              placeholder="Search customers…"
              className="w-full pl-3 pr-8 py-2 bg-[#282828] border border-[#3c3836] rounded-lg text-[#ebdbb2] text-sm placeholder-[#928374] focus:outline-none focus:border-[#fabd2f]"
            />
            <FaSearch className="absolute right-3 top-1/2 -translate-y-1/2 text-[#928374]" size={12} />
          </div>

          {searchOpen && searchQuery.trim() && (
            <div className="mt-1 bg-[#282828] border border-[#3c3836] rounded-lg max-h-64 overflow-y-auto shadow-lg">
              {searchResults.length === 0 ? (
                <div className="px-3 py-2 text-xs text-[#928374] italic">No matches</div>
              ) : (
                searchResults.map(({ customer, onMap }) => (
                  <button
                    key={markerKey(customer)}
                    onClick={() => onMap && selectCustomer(customer)}
                    disabled={!onMap}
                    className={`w-full text-left px-3 py-2 border-b border-[#3c3836] last:border-0 ${
                      onMap ? 'hover:bg-[#3c3836] cursor-pointer' : 'opacity-50 cursor-not-allowed'
                    }`}
                  >
                    <div className="text-sm text-[#ebdbb2] truncate">{customer.fullName || customer.email}</div>
                    <div className="text-[10px] text-[#928374] truncate">
                      {customer.address || [customer.street, customer.suburb].filter(Boolean).join(', ') || '—'}
                      {!onMap && ' · not on map'}
                    </div>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {/* Collapsible sidebar for customers that couldn't be plotted — minimal
          (icon + count badge) by default, expands to the full list. Used to
          live as a block below the map, which ate into map height; moving it
          here keeps the map full-height while still surfacing the data. */}
      <div className={`bg-[#282828] border-l border-[#3c3836] flex flex-col shrink-0 transition-all duration-200 ${unmappedOpen ? 'w-72' : 'w-10'}`}>
        <button
          onClick={() => setUnmappedOpen((o) => !o)}
          className="relative flex items-center justify-center h-10 border-b border-[#3c3836] text-[#928374] hover:text-[#fabd2f] hover:bg-[#3c3836] transition-colors shrink-0"
          title={unmappedOpen ? 'Collapse' : `${unmapped.length} customer${unmapped.length === 1 ? '' : 's'} not shown on the map`}
        >
          {unmappedOpen ? <FaChevronRight size={12} /> : <FaChevronLeft size={12} />}
          {!unmappedOpen && unmapped.length > 0 && (
            <span className="absolute top-1 right-1 min-w-[14px] h-[14px] px-0.5 rounded-full bg-[#fb4934] text-[#1d2021] text-[9px] font-bold flex items-center justify-center">
              {unmapped.length}
            </span>
          )}
        </button>

        {unmappedOpen && (
          <div className="flex-1 overflow-y-auto p-3">
            <div className="flex items-center gap-2 mb-3 text-[#bdae93]">
              <FaExclamationTriangle size={12} />
              <h3 className="text-xs font-semibold uppercase tracking-wide">
                Not shown on map ({unmapped.length})
              </h3>
            </div>
            {unmapped.length === 0 ? (
              <p className="text-xs text-[#928374] italic">Every customer is plotted.</p>
            ) : (
              <div className="space-y-3">
                {unmapped.map(({ customer, reason }) => (
                  <div key={customer.UID || customer.email} className="text-xs border-b border-[#3c3836] pb-2 last:border-0">
                    <div className="text-[#ebdbb2] truncate">{customer.fullName || customer.email}</div>
                    <div className="text-[#928374] italic">{reason}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerMap;
